import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from "@heroicons/react/24/outline";
import logo from "../assets/logo.png";

const STORAGE_KEY = "sidebarCollapsed";

/**
 * The navigation rail shared by the customer and admin sidebars.
 *
 * `items` is the nav definition — `{ to, match, label, icon, badge }` per
 * destination, or `{ divider: true }` for a hairline between groups — so a new
 * destination is one row rather than another copy of this markup.
 *
 * On desktop the rail collapses to an icon strip and remembers that choice
 * across sessions; on mobile it stays a full-width off-canvas panel, where
 * collapsing would leave nothing to tap. The rail sits in the page's flex row,
 * so the content beside it simply reflows — no margin to keep in sync.
 *
 * On desktop it is sticky at full height, so a long page scrolls underneath it
 * rather than carrying the nav off the screen.

 */
const SidebarRail = ({
    items,
    title,
    subtitle,
    subtitleClassName = "text-slate-500",
    isOpen,
    toggleSidebar,
}) => {
    const location = useLocation();
    const [isCollapsed, setIsCollapsed] = useState(() => {
        try {
            return localStorage.getItem(STORAGE_KEY) === "true";
        } catch {
            // Private browsing or blocked storage — start expanded.
            return false;
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(isCollapsed));
        } catch {
            // Not being able to remember the choice must not break the nav.
        }
    }, [isCollapsed]);

    // Longest match wins, so "/admin/solution-images" doesn't light up
    // "solutions" and "/my-credits-history" doesn't light up an earlier prefix.
    const selected =
        items
            .filter((item) => item.match && location.pathname.includes(item.match))
            .map((item) => item.match)
            .sort((a, b) => b.length - a.length)[0] || items.find((item) => item.match)?.match;

    return (
        <div
            className={`fixed inset-y-0 left-0 z-30 flex w-64 flex-col overflow-hidden bg-[#161617] text-slate-300 shadow-[8px_0_40px_-20px_rgba(0,0,0,0.7)] transition-[transform,width] duration-200 ease-in-out md:sticky md:top-0 md:h-screen md:translate-x-0 ${
                isOpen ? "translate-x-0" : "-translate-x-full"
            } ${isCollapsed ? "md:w-[76px]" : "md:w-64"}`}
        >
            <div className={`flex items-center gap-3 py-5 ${isCollapsed ? "md:flex-col md:gap-3 md:px-0" : ""} px-5`}>
                <img
                    src={logo}
                    alt={title}
                    className="h-10 w-10 shrink-0 rounded-[12px] object-contain shadow-lg shadow-black/40"
                />
                <div className={`min-w-0 flex-1 ${isCollapsed ? "md:hidden" : ""}`}>
                    <p className="truncate text-sm font-bold text-white">{title}</p>
                    <p className={`truncate text-[11px] ${subtitleClassName}`}>{subtitle}</p>
                </div>

                {/* Collapse / expand — desktop only; on mobile the rail is an
                    off-canvas panel and this is the close button instead. */}
                <button
                    type="button"
                    onClick={() => setIsCollapsed((current) => !current)}
                    aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                    title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                    className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 text-slate-400 transition hover:border-red-400/40 hover:text-red-300 md:flex"
                >
                    {isCollapsed ? (
                        <ChevronRightIcon className="h-4 w-4" />
                    ) : (
                        <ChevronLeftIcon className="h-4 w-4" />
                    )}
                </button>

                <button
                    type="button"
                    onClick={toggleSidebar}
                    aria-label="Close menu"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 text-slate-400 transition hover:border-red-400/40 hover:text-red-300 md:hidden"
                >
                    <XMarkIcon className="h-4 w-4" />
                </button>
            </div>

            <div className="h-px bg-white/10" />

            <nav className={`vdp-sidebar-scroll flex-1 overflow-y-auto py-4 ${isCollapsed ? "md:px-2" : ""} px-3`}>
                {items.map((item, index) => {
                    if (item.divider) {
                        return <div key={`divider-${index}`} className="my-3 h-px bg-white/10" />;
                    }
                    const Icon = item.icon;
                    const active = selected === item.match;
                    return (
                        <Link
                            key={item.to}
                            to={item.to}
                            onClick={toggleSidebar}
                            aria-current={active ? "page" : undefined}
                            // Collapsed, the label is gone, so the tooltip is the
                            // only thing naming the destination on hover.
                            title={isCollapsed ? item.label : undefined}
                            className={`relative mb-1 flex items-center gap-3 rounded-[12px] px-3 py-2.5 text-sm font-medium transition ${
                                isCollapsed ? "md:justify-center md:px-0" : ""
                            } ${
                                active
                                    ? "bg-white/10 text-white"
                                    : "text-slate-400 hover:bg-white/5 hover:text-white"
                            }`}
                        >
                            {active ? (
                                <span
                                    className={`absolute inset-y-2 left-0 w-1 rounded-full bg-gradient-to-b from-red-500 to-orange-400 ${
                                        isCollapsed ? "md:hidden" : ""
                                    }`}
                                />
                            ) : null}
                            <span className="relative shrink-0">
                                <Icon className="h-5 w-5" />
                                {/* Collapsed, a badge has no room to spell itself
                                    out, so it becomes a dot on the icon. */}
                                {item.badge && isCollapsed ? (
                                    <span className="absolute -right-1 -top-1 hidden h-2 w-2 rounded-full bg-gradient-to-r from-red-500 to-orange-400 md:block" />
                                ) : null}
                            </span>
                            <span className={`flex-1 truncate ${isCollapsed ? "md:hidden" : ""}`}>
                                {item.label}
                            </span>
                            {item.badge ? (
                                <span
                                    className={`rounded-full bg-gradient-to-r from-red-600 to-orange-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white ${
                                        isCollapsed ? "md:hidden" : ""
                                    }`}
                                >
                                    {item.badge}
                                </span>
                            ) : null}
                        </Link>
                    );
                })}
            </nav>
        </div>
    );
};

export default SidebarRail;
